package com.klantenstop.web.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import com.klantenstop.web.components.AdSlot
import com.klantenstop.web.components.H1Heading
import com.klantenstop.web.components.H2Heading
import com.klantenstop.web.components.H3Heading
import com.klantenstop.web.components.KsCategoryCard
import com.klantenstop.web.components.layout.PageLayout
import com.klantenstop.web.components.layout.PageSeoConfig
import com.klantenstop.web.components.search.SearchSection
import com.klantenstop.web.data.models.domain.Category
import com.klantenstop.web.navigation.Navigation
import com.klantenstop.web.styles.Typography
import com.klantenstop.web.utils.Res
import com.klantenstop.web.utils.SiteGlobals
import com.materialkobweb.components.widgets.DsFlexBox
import com.materialkobweb.components.widgets.DsMaterialSymbols
import com.materialkobweb.components.widgets.FilledButtonLink
import com.materialkobweb.components.widgets.OutlinedButtonLink
import com.materialkobweb.styles.MaterialColorVars
import com.materialkobweb.utils.rememberViewModel
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.breakpoint.displayIfAtLeast
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import kotlinx.coroutines.flow.collectLatest
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.keywords.auto
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Text

@Page("/index")
@Composable
fun HomePage() {
    // Use constant key to maintain ViewModel across navigation
    val viewModel = rememberViewModel { HomeViewModel() }
    val state by viewModel.state.collectAsState()

    LaunchedEffect(Unit) {
        viewModel.effect.collectLatest {
            when (it) {
                else -> Unit
            }
        }
    }

    HomePageContent(state)
}

@Composable
private fun HomePageContent(state: HomeContract.State) {
    val breakpoint = rememberBreakpoint()
    PageLayout(
        modifier = Modifier.fillMaxSize(),
        title = "Huisarts, tandarts of kapper nodig? Vind ze hier",
        seo = PageSeoConfig.Managed(
            description = "Zoek snel welke huisartsen, tandartsen en andere zorgverleners nog nieuwe patiënten aannemen. Vermijd patiëntenstop en wachtlijsten met KlantenStop.",
            indexable = true,
            keywords = listOf(
                "dokter",
                "dokter patiëntenstop",
                "huisarts",
                "tandarts",
                "nieuwe patiënt",
            ),
        )
    ) {
        val sectionSpacing = if (breakpoint >= Breakpoint.MD) 140.px else 100.px

        Column(
            modifier = Modifier.fillMaxSize().margin(bottom = sectionSpacing),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(sectionSpacing)
        ) {
            HeroSection(state)

            if (SiteGlobals.environment == "development" || SiteGlobals.adsenseHomeSlotId.isNotBlank()) {
                AdSlot(
                    slotName = "home-mid-content",
                    adUnitId = SiteGlobals.adsenseHomeSlotId,
                    modifier = Modifier
                        .fillMaxWidth()
                        .maxWidth(1000.px)
                        .padding(leftRight = 24.px)
                )
            }

            CategoryGridSection(state.categories)

            AboutSection()
        }
    }
}

@Composable
private fun HeroSection(state: HomeContract.State) {
    val breakpoint = rememberBreakpoint()

    // Hero container with solid background
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .backgroundColor(MaterialColorVars.SurfaceContainer.value())
            .padding(
                topBottom = if (breakpoint >= Breakpoint.MD) 80.px else 48.px,
                leftRight = 24.px
            ),
        contentAlignment = Alignment.Center
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .maxWidth(1200.px),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(if (breakpoint >= Breakpoint.MD) 48.px else 32.px)
        ) {
            // Hero Text Section
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .maxWidth(800.px),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(16.px)
            ) {
                H1Heading(
                    modifier = Modifier.fillMaxWidth(),
                    text = "Wie heeft er een KlantenStop?",
                    color = MaterialColorVars.Primary.value()
                )

                SpanText(
                    modifier = Typography.BodyLarge.toModifier()
                        .fillMaxWidth()
                        .color(MaterialColorVars.OnSurface.value()),
                    text = "Vind, gratis, snel en eenvoudig dienstverleners die nog nieuwe klanten accepteren. Van artsen tot kapsalons, van tandartsen tot nagelstudio's. Geen eindeloos zoeken meer!"
                )
            }

            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(10.px),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(if (breakpoint >= Breakpoint.MD) 80.px else 64.px)
            ) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .margin(top = if (breakpoint >= Breakpoint.MD) 24.px else 36.px)
                        .gap(40.px),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Image(
                        modifier = Modifier
                            .displayIfAtLeast(Breakpoint.MD)
                            .height(auto)
                            .thenIf(breakpoint <= Breakpoint.LG) {
                                Modifier.fillMaxWidth(60.percent)
                            },
                        src = Res.Image.heroImage,
                        alt = "Vacature afbeelding"
                    )

                    // Search Section in Card
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .maxWidth(if (breakpoint >= Breakpoint.MD) 400.px else 100.percent)
                            .backgroundColor(MaterialColorVars.Surface.value())
                            .borderRadius(20.px)
                            .padding(if (breakpoint >= Breakpoint.MD) 40.px else 24.px),
                        contentAlignment = Alignment.Center
                    ) {
                        SearchSection(
                            categories = state.categories,
                            horizontal = false,
                            modifier = Modifier.fillMaxWidth()
                        )
                    }
                }

                // Secondary CTA placed above the fold for better discoverability.
                AddCompanyBanner()
            }
        }
    }
}

/**
 * Homepage grid of highlighted categories, linking each tile to the existing category-filtered
 * search page. Deliberately omits company counts to avoid a per-category aggregation query.
 */
@Composable
private fun CategoryGridSection(categories: List<Category>) {
    val highlightedCategories = categories.filter { it.highlighted }
    if (highlightedCategories.isEmpty()) return

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(leftRight = 24.px),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(32.px)
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .maxWidth(900.px),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(12.px)
        ) {
            H2Heading(
                modifier = Modifier.fillMaxWidth(),
                typography = Typography.HeadlineSmall,
                text = "Verken uitgelichte categorieën",
                color = MaterialColorVars.Primary.value()
            )
        }

        SimpleGrid(
            modifier = Modifier
                .fillMaxWidth()
                .maxWidth(1000.px)
                .gap(20.px),
            numColumns = numColumns(1, 2, 2, 3, 4, 4)
        ) {
            highlightedCategories.forEach { category ->
                KsCategoryCard(category) { text, color ->
                    // Nested under this section's H2, one level down, sized to match the old H5 look
                    H3Heading(text = text, color = color, typography = Typography.TitleMedium)
                }
            }
        }

        OutlinedButtonLink(
            path = Navigation.Screen.Categories.route
        ) {
            Text("Toon alle categorieën")
        }
    }
}

@Composable
private fun AboutSection() {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(leftRight = 24.px),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(48.px)
    ) {
        // Section Title
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .maxWidth(900.px),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(16.px)
        ) {
            H2Heading(
                modifier = Modifier.fillMaxWidth(),
                typography = Typography.HeadlineMedium,
                text = "Waarom KlantenStop?",
                color = MaterialColorVars.Primary.value()
            )

            Typography.BodyMedium
            SpanText(
                modifier = Typography.BodyLarge.toModifier()
                    .fillMaxWidth()
                    .color(MaterialColorVars.OnSurfaceVariant.value()),
                text = "Het probleem is duidelijk: informatie over klantenstops is verspreid, verouderd of moeilijk te vinden. Wij bieden de oplossing."
            )
        }

        // Info Cards Grid
        SimpleGrid(
            modifier = Modifier
                .fillMaxWidth()
                .maxWidth(1000.px)
                .gap(40.px),
            numColumns = numColumns(1, 1, 2, 3)
        ) {
            // Card 1: Problem
            InfoCard(
                icon = "search",
                title = "Het probleem",
                description = "Je verliest kostbare tijd met zoeken op websites en bellen naar dienstverleners die allang een klantenstop hebben."
            )

            // Card 2: Solution
            InfoCard(
                icon = "fact_check",
                title = "Onze oplossing",
                description = "Eén centraal platform waar je direct ziet welke dienstverleners nog nieuwe klanten accepteren."
            )

            // Card 3: Community
            InfoCard(
                icon = "groups",
                title = "Community",
                description = "Onze community en dienstverleners houden de informatie actueel en betrouwbaar."
            )
        }

        // CTA Section
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .maxWidth(800.px)
                .backgroundColor(MaterialColorVars.SurfaceContainer.value())
                .borderRadius(16.px)
                .padding(32.px)
                .margin(top = 56.px),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(24.px)
        ) {
            H3Heading(
                typography = Typography.TitleLarge,
                text = "Klaar om te beginnen?",
                color = MaterialColorVars.Primary.value()
            )

            SpanText(
                modifier = Typography.BodyLarge.toModifier()
                    .color(MaterialColorVars.OnSurface.value()),
                text = "Gebruik de zoekfunctie hierboven of leer meer over hoe KlantenStop werkt."
            )

            SimpleGrid(
                numColumns = numColumns(1, 2),
                modifier = Modifier.fillMaxWidth().gap(16.px)
            ) {
                FilledButtonLink(
                    path = Navigation.Screen.About.route
                ) {
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(8.px)
                    ) {
                        Text("Lees meer over ons")
                        DsMaterialSymbols(
                            modifier = Modifier.fontSize(20.px),
                            icon = "arrow_forward"
                        )
                    }
                }

                FilledButtonLink(
                    path = Navigation.Screen.PatientStopWaitlist.route
                ) {
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(8.px)
                    ) {
                        Text("Patiëntenstop uitgelegd")
                        DsMaterialSymbols(
                            modifier = Modifier.fontSize(20.px),
                            icon = "arrow_forward"
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun AddCompanyBanner() {
    val breakpoint = rememberBreakpoint()
    val isWide = breakpoint > Breakpoint.SM

    DsFlexBox(
        modifier = Modifier
            .fillMaxWidth()
            .borderRadius(20.px)
            .padding(if (isWide) 40.px else 24.px)
            .maxWidth(1000.px)
            .backgroundColor(MaterialColorVars.Surface.value()),
        flexDirection = if (isWide) FlexDirection.Row else FlexDirection.Column,
        alignItems = if (isWide) AlignItems.Center else AlignItems.FlexStart,
        justifyContent = if (isWide) JustifyContent.SpaceBetween else JustifyContent.FlexStart,
        gap = if (isWide) 24.px else 20.px
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(16.px)
        ) {
            DsMaterialSymbols(
                modifier = Modifier
                    .fontSize(36.px)
                    .color(MaterialColorVars.Primary.value()),
                icon = "add_business"
            )
            Column(verticalArrangement = Arrangement.spacedBy(4.px)) {
                H2Heading(
                    typography = Typography.HeadlineSmall,
                    text = "Bedrijf toevoegen",
                    color = MaterialColorVars.Primary.value()
                )
                SpanText(
                    modifier = Modifier
                        .fontSize(if (isWide) 14.px else 13.px)
                        .lineHeight(20.px)
                        .color(MaterialColorVars.OnSurfaceVariant.value()),
                    text = "Staat een bedrijf er nog niet bij? Iedereen kan het gratis toevoegen aan ons platform."
                )
            }
        }

        OutlinedButtonLink(
            modifier = Modifier.thenIf(!isWide) { Modifier.fillMaxWidth() },
            path = Navigation.Screen.ManageCompany().route
        ) {
            Text("Bedrijf toevoegen")
        }
    }
}

@Composable
private fun InfoCard(
    icon: String,
    title: String,
    description: String
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(24.px)
            .borderRadius(16.px)
            .backgroundColor(MaterialColorVars.SurfaceContainer.value()),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(16.px)
    ) {
        // Icon
        DsMaterialSymbols(
            modifier = Modifier
                .fontSize(48.px)
                .color(MaterialColorVars.Primary.value()),
            icon = icon
        )

        // Title
        H3Heading(
            typography = Typography.HeadlineSmall,
            text = title,
            color = MaterialColorVars.Primary.value()
        )

        // Description
        SpanText(
            modifier = Typography.BodyMedium.toModifier()
                .color(MaterialColorVars.OnSurface.value()),
            text = description
        )
    }
}
